import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Tab,
  Tabs,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { View, Text } from '@aws-amplify/ui-react';
import { useTheme } from '../../context/PermissionsContext/ThemeContext/ThemeContext';
import { GamifyToast } from '../../common/CustomToasts';

// Styled components using theme
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  backgroundColor: theme.custom.background2,
  borderRadius: '8px',
  boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
  margin: '20px 0',
}));

const StyledTable = styled(Table)(() => ({
  minWidth: 650,
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: theme.custom.primary,
}));

const StyledHeaderCell = styled(TableCell)(({ theme }) => ({
  color: theme.custom.textCTA,
  fontWeight: 'bold',
  fontSize: '1rem',
  padding: '16px',
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.custom.background3,
  },
  '&:hover': {
    backgroundColor: theme.custom.primaryLight,
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: '12px 16px',
  color: theme.custom.text,
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  backgroundColor: theme.custom.background2,
  borderRadius: '8px',
  marginBottom: '20px',
  '& .MuiTabs-indicator': {
    backgroundColor: theme.custom.primary,
  },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  color: theme.custom.textLight,
  fontWeight: 'bold',
  '&.Mui-selected': {
    color: theme.custom.primary,
  },
}));

export default function HourlyTableView({ reports, userIds }) {
  const { theme } = useTheme();
  const [selectedDay, setSelectedDay] = useState(0);
  const [pinData, setPinData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch pin data when component mounts
  useEffect(() => {
    const fetchPinData = async () => {
      // If we already have full_hourly_report data, use it
      if (reports?.full_hourly_report) {
        console.log('Using existing full_hourly_report data');
        setPinData(reports.full_hourly_report);
        return;
      }

      // Otherwise, fetch the data
      if (!reports || !reports.reports || reports.reports.length === 0) {
        setError('No report data available');
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const url = 'https://api.gogamify.com/report/kpireport';

        // Get date range from the first report
        const firstReport = reports.reports[0];
        const lastReport = reports.reports[reports.reports.length - 1];

        // Extract dates from report names (e.g., "2024-10-13")
        const fromDate = firstReport.report_info.report_name;
        const toDate = lastReport.report_info.report_name;

        // Create date objects
        const fromTimeUTC = new Date(`${fromDate}T00:00:00Z`);
        const toTimeUTC = new Date(`${toDate}T23:59:59Z`);

        let allRecords = [];
        let currentWeekStart = new Date(fromTimeUTC);

        while (currentWeekStart < toTimeUTC) {
          const currentWeekEnd = new Date(currentWeekStart);
          currentWeekEnd.setDate(currentWeekEnd.getDate() + 6);
          if (currentWeekEnd > toTimeUTC) {
            currentWeekEnd.setTime(toTimeUTC.getTime());
          }

          let hasMoreData = true;
          let nextToken = null;

          while (hasMoreData) {
            const payload = {
              report_type: 'hourly-export',
              org_id: reports.org_id,
              users: userIds && userIds.length > 0 ? userIds : undefined,
              dates: {
                from: currentWeekStart.toISOString(),
                to: currentWeekEnd.toISOString(),
              },
              pageSize: 500,
              nextToken,
            };

            const response = await fetch(url, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload),
            });

            if (!response.ok) {
              throw new Error(`API Error: ${response.status}`);
            }

            const data = await response.json();

            if (data.records && data.records.length > 0) {
              allRecords = [...allRecords, ...data.records];
            }

            if (data.nextToken) {
              try {
                const tokenValue = parseInt(data.nextToken, 10);
                if (!isNaN(tokenValue)) {
                  const tokenDate = new Date(tokenValue);
                  const currentDate = new Date();
                  if (tokenDate > currentDate) {
                    hasMoreData = false;
                  } else {
                    nextToken = data.nextToken;
                  }
                } else {
                  nextToken = data.nextToken;
                }
              } catch (error) {
                console.error('Error parsing nextToken:', error);
                nextToken = data.nextToken;
              }
            } else {
              hasMoreData = false;
            }
          }

          currentWeekStart = new Date(
            currentWeekStart.getTime() + 7 * 24 * 60 * 60 * 1000
          );
        }

        console.log('Fetched pin data records:', allRecords.length);
        setPinData(allRecords);
      } catch (err) {
        console.error('Error fetching pin data:', err);
        setError('Failed to load pin data. Please try again.');
        GamifyToast.error('Error', 'Failed to load pin data for table view.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchPinData();
  }, [reports, userIds]);

  // Organize pin records by day
  const recordsByDay = useMemo(() => {
    if (!pinData || pinData.length === 0) {
      return [];
    }

    // Group records by date
    const grouped = {};
    pinData.forEach((record) => {
      const date = record.pin_date;
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(record);
    });

    // Sort dates and convert to array format
    const sortedDates = Object.keys(grouped).sort();
    return sortedDates.map((date) => ({
      date,
      records: grouped[date].sort((a, b) => {
        // Sort by time within each day
        return a.pin_time.localeCompare(b.pin_time);
      }),
    }));
  }, [pinData]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setSelectedDay(newValue);
  };

  // Show loading state
  if (isLoading) {
    return (
      <Box
        style={{
          width: '100%',
          padding: '40px',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '20px',
        }}
      >
        <CircularProgress size={60} style={{ color: theme.primary }} />
        <Text
          style={{
            fontSize: '1.2rem',
            color: theme.text,
            fontWeight: 'bold',
          }}
        >
          Loading pin data...
        </Text>
        <Text
          style={{
            fontSize: '1rem',
            color: theme.textLight,
          }}
        >
          This may take a moment for large date ranges.
        </Text>
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box
        style={{
          width: '100%',
          padding: '40px',
          textAlign: 'center',
        }}
      >
        <Text
          style={{
            fontSize: '1.2rem',
            color: 'red',
          }}
        >
          {error}
        </Text>
      </Box>
    );
  }

  // Show no data state
  if (!recordsByDay || recordsByDay.length === 0) {
    return (
      <Box
        style={{
          width: '100%',
          padding: '40px',
          textAlign: 'center',
        }}
      >
        <Text
          style={{
            fontSize: '1.2rem',
            color: theme.textLight,
          }}
        >
          No pin data available for the selected period.
        </Text>
      </Box>
    );
  }

  const currentDayData = recordsByDay[selectedDay];

  return (
    <View style={{ width: '100%', padding: '0 20px' }}>
      {/* Tabs for day selection */}
      <StyledTabs
        value={selectedDay}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
      >
        {recordsByDay.map((dayData) => (
          <StyledTab
            key={dayData.date}
            label={`${dayData.date} (${dayData.records.length} pins)`}
          />
        ))}
      </StyledTabs>

      {/* Table for selected day */}
      {currentDayData && (
        <StyledTableContainer component={Paper}>
          <StyledTable>
            <StyledTableHead>
              <TableRow>
                <StyledHeaderCell>#</StyledHeaderCell>
                <StyledHeaderCell>User Name</StyledHeaderCell>
                <StyledHeaderCell>Date</StyledHeaderCell>
                <StyledHeaderCell>Time</StyledHeaderCell>
                <StyledHeaderCell>Pin Number</StyledHeaderCell>
                <StyledHeaderCell>Address</StyledHeaderCell>
                <StyledHeaderCell>Distance From Door (meters)</StyledHeaderCell>
              </TableRow>
            </StyledTableHead>
            <TableBody>
              {currentDayData.records.map((record, index) => (
                <StyledTableRow
                  key={`${record.pin_date}-${record.pin_time}-${index}`}
                >
                  <StyledTableCell>{index + 1}</StyledTableCell>
                  <StyledTableCell>{record.user_name}</StyledTableCell>
                  <StyledTableCell>{record.pin_date}</StyledTableCell>
                  <StyledTableCell>{record.pin_time}</StyledTableCell>
                  <StyledTableCell>{record.pin}</StyledTableCell>
                  <StyledTableCell>{record.address}</StyledTableCell>
                  <StyledTableCell>
                    {record.distance_from_door || 'Not Recorded'}
                  </StyledTableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </StyledTable>
        </StyledTableContainer>
      )}

      {/* Summary info */}
      <Box
        style={{
          textAlign: 'center',
          marginTop: '20px',
          marginBottom: '20px',
          color: theme.textLight,
        }}
      >
        <Text>
          Showing {currentDayData.records.length} pin records for{' '}
          {currentDayData.date}
        </Text>
      </Box>
    </View>
  );
}
